import { BooleanResolver, MapperFn, Optional, Supplier, ValueResolver } from '../../../core/utils/his-type.utils';
import { HisTreeNode, HisTreeNodeData } from '../../model/his-tree.model';
import { HisTreeCopyCutDirective } from './his-tree-copy-cut.directive';

type FocusTargetRoot = 'document' | 'table' | 'host';

/**
 * Keys that identify the six fixed default action groups.
 *
 * Render order (fixed):
 *   editView → copyCut  → [custom slot] → expandCollapse → addActions → moveActions → delete
 */
export type DefaultGroupKey =
    | 'editView'
    | 'copyCut'
    | 'expandCollapse'
    | 'addActions'
    | 'moveActions'
    | 'delete';

/** IDs of the built-in default actions, in render order within their group */
export type DefaultActionId = DefaultSingleActionId | DefaultMultiActionId;

export type DefaultSingleActionId =
    | 'edit' | 'view'
    | 'addAbove' | 'addBelow' | 'addChild'
    | 'moveUp' | 'moveDown'
    | 'delete';

export type DefaultMultiActionId = 'copy' | 'cut' | 'paste' | 'cancelCopy' | 'cancelCut' | 'expandAll' | 'collapseAll';

/** Slot-IDs of the built-in default actions, in render order within their group */
export type DefaultActionSlotId = DefaultSingleActionId | DefaultMultiActionOverride['slot'];

export type DefaultActionOverride<T extends HisTreeNodeData = HisTreeNodeData> = DefaultActionBaseOverride<DefaultSingleActionId, T> | DefaultMultiActionOverride<T>;

export type DefaultMultiActionOverride<T extends HisTreeNodeData = HisTreeNodeData> = {
    slot: 'copyPaste', actions: DefaultActionBaseOverride<'copy' | 'cancelCopy', T>[]
} | {
    slot: 'cutPaste', actions: DefaultActionBaseOverride<'cut' | 'paste' | 'cancelCut', T>[]
} | {
    slot: 'expandCollapse', actions: DefaultActionBaseOverride<'expandAll' | 'collapseAll', T>[]
}

/** Override options for any built-in default action */
export interface DefaultActionBaseOverride<ID extends DefaultActionId = DefaultActionId, T extends HisTreeNodeData = HisTreeNodeData> extends UniversalTreeActionDefProperties<T> {
    /** The ID of the default action being overridden */
    id: ID;
    /** Replaces the built-in icon */
    icon?: string;
    /** Replaces the built-in i18n title key */
    title?: string;
}

/** A group of custom actions inserted at the single fixed middle slot */
export interface CustomActionGroup<T extends HisTreeNodeData = HisTreeNodeData> {
    /**
     * Define a custom unique key for this action group. If not set, an index based key will be generated. (`custom_<index>`)\
     * The index only counts the custom groups.
     */
    key?: string;
    /** List of custom action-slots within this group */
    slots: TreeActionDef<T>[];
}

/**
 * Configuration object for HisTreeActionButtonsComponent.
 *
 * All properties are optional; omitting them retains built-in defaults.
 *
 * @example
 * ```ts
 * config: TreeActionButtonsConfig = {
 *   defaults: [ 'view', 'expandAll', 'collapseAll', 'addChild', 'moveUp', 'moveDown',
 *     { id: 'edit', routeAction: idAwareRouteAction('/entities/:id') },
 *     { id: 'delete', visible: ctx => ctx.rowData.canDelete },
 *   ],
 *   appendToGroup: {
 *     editView: [{ id: 'preview', icon: 'EYE', title: 'common.previewOf' }],
 *   },
 *   customGroups: [{ 
 *     key: 'download',
 *     slots: [{ id: 'download', icon: 'IMPORT', title: 'common.downloadOf' }]
 *   }],
 * };
 * ```
 */
export interface TreeActionButtonsConfig<T extends HisTreeNodeData = HisTreeNodeData> {
    /**
     * Enable built-in default actions by adding their IDs to this list.\
     * Allows per-action overrides for every action by specifying an object with the action ID as the key.\
     * Unspecified properties retain their built-in defaults.
     */
    defaults?: ( DefaultActionSlotId | DefaultActionOverride<T> )[];
    /**
     * Custom actions appended to the end of an existing default group identified via its group key.\
     * Appended actions emit via (actionTriggered).
     */
    appendToGroup?: Partial<Record<DefaultGroupKey, TreeActionDef<T>[]>>;
    /**
     * Custom groups inserted at the single fixed middle slot
     * (between 'cut' and 'expandCollapse').
     */
    customGroups?: CustomActionGroup<T>[];
}

/** Row context supplied to visibility, disabled, and route predicate functions */
export interface TreeActionContext<T extends HisTreeNodeData = HisTreeNodeData> {
    /** The row data for this tree node (`rowNode.node.data`) */
    rowData: T;
    /** The tree node instance associated with this row (`rowNode.node`) */
    node: HisTreeNode<T>;
    /** Display label representing the row, e.g., a person's name */
    label?: string;
    /** Additional extensible metadata for custom evaluations */
    customMeta: Record<string, unknown>;
}

/** Context type for per-action ng-template overrides */
export interface ActionTemplateContext {
    /** Fully resolved render state for this action */
    $implicit: ActionRenderModel;
}

/** Definition for a fully custom action */
export type TreeActionDef<T extends HisTreeNodeData = HisTreeNodeData> = TreeSingleActionDef<T> | TreeMultiActionDef<T>;

export interface TreeSingleActionDef<T extends HisTreeNodeData = HisTreeNodeData> extends TreeActionBaseDef<T> {
    /** Whether this action is rendered.\
     * Defaults to `true` */
    rendered?: BooleanResolver<TreeActionContext<T>>;
}

export interface TreeMultiActionDef<T extends HisTreeNodeData = HisTreeNodeData> {
    /** The slot identifier for this multi-state-action */
    slot: string;
    /** The individual actions that make up this multi-state-action */
    actions: TreeActionBaseDef<T>[]
    /** Whether this action is rendered.\
     * Defaults to `true` */
    rendered?: BooleanResolver<TreeActionContext<T>>;
}

export interface TreeActionBaseDef<T extends HisTreeNodeData = HisTreeNodeData> extends UniversalTreeActionDefProperties<T> {
    /** Unique identifier for this action */
    id: string;
    /** The `hisImage` icon for this action */
    icon: string;
    /**
     * i18n key passed through the hisUi pipe.\
     * A row label (if resolved) is supplied as a parameter to the pipe for specific translations,
     * e.g. "'{0}' bearbeiten" (`'common.editOf' | hisUi: <label>`).
     */
    title: string;
    /**
     * Scope of the action's dependencies, either per row or shared across the tree.\
     * Modifies the behavior of the state evaluation for optimal performance.\
     * Defaults to `row` (per row).
     */
    dependencyScope?: 'row' | 'shared';
}

interface UniversalTreeActionDefProperties<T extends HisTreeNodeData = HisTreeNodeData> {
    /** Additional CSS class(es) appended to the element */
    cssClass?: string | string[];
    /**
     * Controls whether the action is displayed (a spacer occupies its slot when false).\
     * In multi-state-actions, this controls the visibility of the individual action within its slot. The first visible action in the slot will be displayed.
     * Defaults to `true`.
     */
    visible?: BooleanResolver<TreeActionContext<T>>;
    /**
     * Controls whether the action is non-interactive.\
     * Defaults to `false`.
     */
    disabled?: BooleanResolver<TreeActionContext<T>>;
    /**
     * Converts the action into a router-link.
     * Mutually exclusive: when set, the action navigates and does **NOT** emit any output.
     */
    routeAction?: ValueResolver<string | any[], TreeActionContext<T>>;
    /**
     * The CSS selector or HTMLElement mapper of the target that should receive focus after this action is triggered.
     * Will be used as fallback when `implicitRestoreFocus` is true, or as the primary target if `implicitRestoreFocus` is false.
     * The mapper function receives the resolved `targetRoot` as base for a querySelector.
     */
    focusTarget?: string | MapperFn<HTMLElement>;
    /**
     * Specifies the root element for the focus target. This is used in conjunction with `focusTarget` to determine where focus should be restored after the action is triggered.
     * - `document` (Default): The root element is the entire document.
     * - `table`: The root element is the table containing the action button.
     * - `self`: The root element is the component containing the action button.
     */
    focusTargetRoot?: FocusTargetRoot;
    /**
     * Automatically restore focus to this element after this action is triggered. (Default: `true`)\
     * Set to false if focus is handled explicitly elsewhere, to prevent unexpected behavior.
     */
    implicitRestoreFocus?: boolean;
}

/** Fully resolved, render-ready model for one action */
export interface ActionRenderModel {
    /** The identity-related information for this action */
    readonly identity: ActionIdentity;
    /** The presentation-related information for this action, including css and uitexts */
    readonly presentation: ActionPresentation;
    /** The current state of this action, including visibility and interactivity */
    readonly state: ActionState;
    /** The focus-related information for this action */
    readonly focus: ActionFocus;
    /**
     * Present when this action is a router-link.
     * Absent for callback-mode actions (button).
     */
    readonly routeAction?: string | any[];
}

interface ActionIdentity {
    /** The ID specific to this action, as set by the config. (shared across all tree-action-buttons instances) */
    readonly id: string;
    /** The resolved slot ID for this action, combining multiple individual action states if applicable. (shared across all tree-action-buttons instances) */
    readonly slotId: string;
    /** The group key this action belongs to. (shared across all tree-action-buttons instances) */
    readonly groupKey: string;
    /** The unique identifier for the DOM element associated with this action. (unique across the page) */
    readonly elementUid: string;
    /** Indicates whether this action is a built-in default action */
    readonly isDefault: boolean;
}

interface ActionPresentation {
    /** `hisImage` key */
    readonly icon: string;
    /** Raw i18n key, piped through hisUi in templates to allow for a dynamic label parameter */
    readonly title: string;
    /** Display label representing the row, e.g. a person's name */
    readonly label: string | undefined;
    readonly cssClass: string;
}

interface ActionState {
    readonly visible: Supplier<boolean>;
    readonly disabled: Supplier<boolean>;
    readonly rendered: Supplier<boolean>;
}

interface ActionFocus {
    /**
     * The CSS selector or HTMLElement mapper of the target that should receive focus after this action is triggered.
     * Will be used as fallback when `restoreSelf` is true, or as the primary target if `restoreSelf` is false.
     * The mapper function receives the resolved `targetRoot` as base for a querySelector.
     */
    readonly target: string | MapperFn<HTMLElement> | undefined;
    /** Specifies the root element for the focus target. This is used in conjunction with `target` to determine where focus should be restored after the action is triggered.
     * - `document` (Default): The root element is the entire document.
     * - `table`: The root element is the table containing the action button.
     * - `self`: The root element is the component containing the action button.
     */
    readonly targetRoot: FocusTargetRoot;
    /** Whether the focus should be automatically restored to the target after the action is completed. */
    readonly restoreSelf: boolean;
}

/** Payload emitted by all action event outputs */
export interface TreeActionEvent<T extends HisTreeNodeData = HisTreeNodeData> {
    /** The ID of the action that was triggered */
    readonly actionId: string;
    /** The group key this action belongs to */
    readonly groupKey: string;
    /** The resolved label of the tree node associated with the action */
    readonly nodeLabel: string | undefined;
    /** The data of the row associated with the action */
    readonly rowData: T;
    /** The tree node associated with the action */
    readonly node: HisTreeNode<T>;
    /** The original DOM event that triggered the action */
    readonly originalEvent: Event;
}

/** Internal: a resolved group ready for rendering */
export interface ResolvedActionGroup {
    readonly key: string;
    readonly actions: ActionRenderModel[];
}


// Helper functions
type NodeBearer = Pick<TreeActionContext, 'node'>;
type RowBearer = Pick<TreeActionContext, 'rowData'>;
type MetaBearer = Pick<TreeActionContext, 'customMeta'>;

export function isFirstNode( ctx: NodeBearer ): boolean {
    return ctx.node.isFirst;
}

export function isLastNode( ctx: NodeBearer ): boolean {
    return ctx.node.isLast;
}

export function isExpanded( ctx: NodeBearer ): boolean {
    return !!ctx.node.expanded;
}

export function hasChildren( ctx: NodeBearer ): boolean {
    return !!ctx.node.children && ctx.node.children.length > 0;
}

export function rowId( ctx: RowBearer ): number {
    return ctx.rowData.id;
}

function copyCutMode( { customMeta }: MetaBearer ) {
    return customMeta.copyCutMode instanceof HisTreeCopyCutDirective ? customMeta.copyCutMode : null;
}

export function isCutMode( ctx: MetaBearer ): boolean {
    const state = copyCutMode( ctx )?.state();
    return !!state && state.cut !== null;
}

export function isCopySource( ctx: MetaBearer & NodeBearer ): boolean {
    const state = copyCutMode( ctx )?.state();
    return !!state && ( state.copy?.sourceNode === ctx.node );
}

export function isCutSource( ctx: MetaBearer & NodeBearer ): boolean {
    const state = copyCutMode( ctx )?.state();
    return !!state && ( state.cut?.sourceNode === ctx.node );
}

export function canCut( ctx: TreeActionContext ): Optional<boolean> {
    return copyCutMode( ctx )?.canCut( ctx );
}

export function canPaste( ctx: TreeActionContext ): Optional<boolean> {
    return copyCutMode( ctx )?.canPaste( ctx );
}

export function idAwareRouteAction( route: string ): ( ctx: RowBearer ) => string | any[] {
    return ( ctx: RowBearer ) => {
        const id = rowId( ctx );
        if ( route.includes(':id') ) {
            return route.replace( ':id', String( id ) );
        }
        return [route, id];
    };
}
